import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarRange, Clock3, ListFilter, MonitorPlay, Send, X } from 'lucide-react'
import { cgOverlaysApi, cgTemplatesApi } from '../../lib/api.js'
import type { CgOverlay, CgOverlayUpcomingPassage, CgTemplate } from '../../types/index.js'
import { Spinner } from '../common/Spinner.js'
import { CgTemplateThumbnail } from './CgTemplateThumbnail.js'
import { TestChannelPlayer } from '../test-channel/TestChannelPlayer.js'
import { openSettingsTab } from '../../lib/appNavigation.js'
import { useHasPermission } from '../../store/index.js'
import { PERIOD_STATUS_LABELS, periodsSummary, periodsTimeline } from './cgOverlayValidity.js'

const PREVIEW_DELAY_MS = 400

export type PreviewRule = Pick<CgOverlay,
  'anchor' | 'offsetSec' | 'durationSec' | 'repeatIntervalSec' | 'hideBeforeEndSec'
  | 'programScope' | 'targetSeriesIds' | 'targetProgramIds' | 'targetCategoryIds' | 'validityPeriods'>

const parisDay = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short' })
const parisTime = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })

interface Props {
  active: boolean
  channelId: string
  template: CgTemplate | null
  rule: PreviewRule
  /** Le brouillon ne s'enregistrerait pas : on n'interroge pas la grille. */
  ruleReady: boolean
  timingSentence: string
  scopeSentence: string
}

/**
 * Panneau de controle de l'onglet Diffusion : ce que la regle enverra, et
 * quand. Tout y est en lecture seule, sauf « Tester », qui sort le modele sur
 * le canal CasparCG de test — jamais sur le canal de diffusion — et dont la
 * réplique 1080p50 s'affiche juste dessous.
 */
export const CgOverlayControlPanel = memo(function CgOverlayControlPanel({
  active, channelId, template, rule, ruleReady, timingSentence, scopeSentence,
}: Props) {
  const [passages, setPassages] = useState<CgOverlayUpcomingPassage[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [testing, setTesting] = useState<'idle' | 'sending' | 'shown' | 'hiding'>('idle')
  const [testMessage, setTestMessage] = useState('')
  const request = useRef(0)
  const canOpenTestChannel = useHasPermission('TECHNICAL_SETTINGS_MANAGE')
  const openTestChannel = useCallback(() => openSettingsTab('test-channel'), [])
  const ruleKey = JSON.stringify(rule)
  const now = useMemo(() => new Date(), [])
  const timeline = useMemo(() => periodsTimeline(rule.validityPeriods, now), [now, rule.validityPeriods])

  useEffect(() => {
    const current = ++request.current
    if (!active || !ruleReady) { setLoading(false); return }
    setLoading(true)
    const timer = window.setTimeout(() => {
      cgOverlaysApi.previewPassages({ channelId, rule: JSON.parse(ruleKey) as PreviewRule })
        .then(result => { if (current === request.current) { setPassages(result); setError('') } })
        .catch((caught: Error) => { if (current === request.current) setError(caught.message || 'Calcul impossible pour le moment.') })
        .finally(() => { if (current === request.current) setLoading(false) })
    }, PREVIEW_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [active, channelId, ruleKey, ruleReady])

  // Le test suit le modele : en changer retire l'etat « envoye ».
  useEffect(() => { setTesting('idle'); setTestMessage('') }, [template?.id])

  const toggleTest = useCallback(async () => {
    if (!template) return
    const showing = testing === 'shown'
    setTesting(showing ? 'hiding' : 'sending')
    setTestMessage('')
    try {
      if (showing) await cgTemplatesApi.hideTest(channelId, template.id)
      else await cgTemplatesApi.test(channelId, template.id)
      setTesting(showing ? 'idle' : 'shown')
      setTestMessage(showing ? 'Test retiré du canal 2.' : 'Modèle envoyé sur le canal 2.')
    } catch (caught) {
      setTesting(showing ? 'shown' : 'idle')
      setTestMessage(caught instanceof Error ? caught.message : 'Envoi impossible.')
    }
  }, [channelId, template, testing])

  return (
    <aside className="cg-control-panel" aria-label="Contrôle de la diffusion">
      <section className="cg-control-block">
        <div className="cg-control-heading">
          <strong><MonitorPlay size={14} aria-hidden="true" />Aperçu antenne</strong>
          {template?.cgLayer != null && <span className="cg-control-chip">Layer CG {template.cgLayer}</span>}
        </div>
        <div className="cg-control-preview">
          {template ? <CgTemplateThumbnail template={template} /> : <span>Chemin HTML sans modèle : pas d'aperçu.</span>}
        </div>
        {template && <p className="cg-control-caption">Rendu du modèle « {template.name} » avec ses valeurs par défaut.</p>}
      </section>

      <ul className="cg-control-summary" aria-label="Résumé de la diffusion">
        <li><Clock3 size={13} aria-hidden="true" />{timingSentence}</li>
        <li><ListFilter size={13} aria-hidden="true" />{scopeSentence}</li>
        <li><CalendarRange size={13} aria-hidden="true" />{periodsSummary(rule.validityPeriods, now)}</li>
      </ul>

      <div className="cg-control-test">
        <button
          type="button"
          className="cg-button"
          onClick={toggleTest}
          disabled={!template || testing === 'sending' || testing === 'hiding'}
          title="Sort le modèle enregistré sur le canal CasparCG de test 2, jamais à l'antenne"
        >
          {testing === 'shown' ? <X size={14} aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
          {testing === 'sending' ? 'Envoi…' : testing === 'hiding' ? 'Retrait…' : testing === 'shown' ? 'Retirer le test' : 'Tester sur le canal 2'}
        </button>
        {testMessage && <span className="cg-control-caption" role="status">{testMessage}</span>}
        {/* Réplique du canal 2 : ce que « Tester » a réellement sorti, son
            coupé par défaut (un poste de préparation n'est pas une régie). */}
        {active && (
          <TestChannelPlayer
            variant="compact"
            occupied={testing === 'shown'}
            sourceLabel={testing === 'shown' && template ? `${template.name} · habillage seul` : null}
            onOpenTestChannel={canOpenTestChannel ? openTestChannel : undefined}
          />
        )}
      </div>

      {rule.validityPeriods.length > 0 && (
        <section className="cg-control-block" aria-labelledby="cg-coverage-title">
          <h4 id="cg-coverage-title" className="cg-control-label">Couverture des périodes</h4>
          <div className="cg-coverage">
            <div className="cg-coverage__months" aria-hidden="true">
              {timeline.months.map(month => <span key={`${month.label}-${month.left}`} style={{ left: `${month.left}%` }}>{month.label}</span>)}
            </div>
            {timeline.bars.map(bar => (
              <div className="cg-coverage__row" key={bar.index}>
                <div className="cg-coverage__track">
                  <span
                    className={`cg-coverage__bar is-${bar.status.toLowerCase()}${bar.openStart ? ' is-open-start' : ''}${bar.openEnd ? ' is-open-end' : ''}`}
                    style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                  />
                  {timeline.now !== null && <span className="cg-coverage__now" style={{ left: `${timeline.now}%` }} aria-hidden="true" />}
                </div>
                <small>Période {bar.index + 1} · {PERIOD_STATUS_LABELS[bar.status]}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="cg-control-block" aria-labelledby="cg-passages-title">
        <h4 id="cg-passages-title" className="cg-control-label">
          Prochains passages {loading && <Spinner />}
        </h4>
        {!ruleReady && <p className="cg-control-caption">Complétez les programmes concernés et les périodes pour calculer les passages.</p>}
        {ruleReady && error && <p className="cg-control-caption is-error" role="alert">{error}</p>}
        {ruleReady && !error && passages && passages.length === 0 && !loading && (
          <p className="cg-control-caption">Aucun passage sur les 7 prochains jours de grille.</p>
        )}
        {ruleReady && passages && passages.length > 0 && (
          <ol className="cg-passages">
            {passages.map(passage => {
              const due = new Date(passage.dueAt)
              return (
                <li key={passage.itemId}>
                  <span className="cg-passages__day">{parisDay.format(due)}</span>
                  <span className="cg-passages__time">{parisTime.format(due)}</span>
                  <span className="cg-passages__title">
                    {passage.title ?? 'Sans titre'}
                    {passage.occurrenceCount > 1 && <small> · {passage.occurrenceCount} passages</small>}
                  </span>
                </li>
              )
            })}
          </ol>
        )}
        <p className="cg-control-caption">Calculé sur la grille avec les réglages en cours, même non enregistrés.</p>
      </section>
    </aside>
  )
})
